import type { FrameMenuItem } from 'app/types'
import { $T } from 'client/library/translation'
import type { ServiceInitArgs } from 'app/services/base'
import { documentKit, useMindMap } from 'compositions/mindmap'
import { isActionEnabled } from 'compositions/commands'
import { useWorkspaceStore } from 'app/services/pinia/workspace-store'
import { isPhoneOnly } from 'utils/detectDevice'
import { trackNewVersionWorks } from 'client/library/tracker'
import { useContextStore } from 'client/store/context-store'
import { useShortcutManagersStore } from 'app/services/pinia/shortcut-managers-store'
import { useGlobalConfig } from '../../composables/use-global-config'

export function buildEmbedTopicMenu(services: ServiceInitArgs, args: { currentTargetTopicType: documentKit.TopicType }) {
  const { action } = services
  if (!action) {
    return []
  }

  const workbook = useWorkspaceStore().workbook
  const { contexts } = useContextStore()
  const { embedEditorPreset } = useGlobalConfig()
  if (!workbook) return []

  const { currentTargetTopicType } = args
  const shortcutManager = useShortcutManagersStore().getShortcutManager('sharing-editor')

  const toggleBranchLabel = isActionEnabled('mutate:unfoldBranch') ? $T('Unfold Subtopic') : $T('Fold Subtopic')
  const toggleAllBranchLabel = isActionEnabled('mutate:foldAllDescendantBranches') ? $T('Fold All Sub-Branches') : $T('Unfold All Sub-Branches')

  const topicFoldMenu: FrameMenuItem[] = [
    {
      label: toggleBranchLabel,
      enable: workbook.getAction('editor.toggleBranch').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.toggleBranch')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('foldSubtopic')
        await workbook.getAction('editor.toggleBranch').execute()
      },
    },
  ]

  const topicFoldAllMenu: FrameMenuItem[] = [
    {
      label: toggleAllBranchLabel,
      enable: workbook.getAction('editor.toggleAllBranch').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.toggleAllBranch')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('unfoldAllSubBranches')
        await workbook.getAction('editor.toggleAllBranch').execute()
      },
    },
  ]

  const createSheetFromSelectedMenu: FrameMenuItem[] = [
    {
      label: $T('New Sheet From Topic'),
      enable: workbook.getAction('editor.newSheetFromTopic').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.newSheetFromTopic')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('newSheetFromTopic')
        await workbook.getAction('editor.newSheetFromTopic').execute()
      },
    },
  ]

  const makeInsertMenu = (): FrameMenuItem[] => {
    const isMultipleSelection = useMindMap().selection.value?.isMulti
    const subMenuItems: FrameMenuItem[] = [
      embedEditorPreset.enableNoteLabel
        ? {
            label: $T('Note'),
            enable: action.ui.openNotePopover.isEnabled && !isMultipleSelection,
            shortcut: shortcutManager?.findDescriptionByAction(action.ui.openNotePopover),
            action: async () => {
              trackNewVersionWorks.editorRightClickInsert('note')
              await action.ui.openNotePopover.execute({ status: true })
            },
          }
        : null,
      embedEditorPreset.enableNoteLabel
        ? {
            label: $T('Label'),
            enable: action.ui.openLabelPopover.isEnabled,
            shortcut: shortcutManager?.findDescriptionByAction(action.ui.openLabelPopover),
            action: async () => {
              trackNewVersionWorks.editorRightClickInsert('label')
              await action.ui.openLabelPopover.execute({ status: true })
            },
          }
        : null,
      {
        label: $T('Callout'),
        enable: workbook.getAction('editor.addCallout').isEnabled && !isMultipleSelection,
        shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.addCallout')),
        action: async () => {
          trackNewVersionWorks.editorRightClickInsert('callout')
          await workbook.getAction('editor.addCallout').execute()
        },
      },
    ].filter(Boolean) as FrameMenuItem[]
    return [
      {
        label: $T('Insert'),
        enable: subMenuItems.some(item => item.enable),
        subMenuItems,
      },
    ]
  }

  const topicMenu: FrameMenuItem[] = [
    {
      label: $T('Copy'),
      enable: workbook.getAction('copy').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('copy')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('copy')
        await workbook.getAction('copy').execute()
      },
    },
    {
      label: $T('Cut'),
      enable: workbook.getAction('cut').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('cut')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('cut')
        await workbook.getAction('cut').execute()
      },
    },
    {
      label: $T('Paste'),
      enable: workbook.getAction('paste').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('paste')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('paste')
        await workbook.getAction('paste').execute()
      },
    },
    {
      label: $T('Duplicate'),
      enable: workbook.getAction('duplicate').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('duplicate')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('duplicate')
        await workbook.getAction('duplicate').execute()
      },
    },
  ]

  const separatorMenu: FrameMenuItem[] = [
    {
      type: 'separator',
    },
  ]

  const deleteItemMenu: FrameMenuItem[] = [
    {
      label: $T('Delete'),
      enable: workbook.getAction('delete').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('delete')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('delete')
        await workbook.getAction('delete').execute()
      },
    },
  ]

  const deleteSingleTopicMenu: FrameMenuItem[] = [
    {
      label: $T('Delete Single Topic'),
      enable: workbook.getAction('deleteSingleTopic').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('deleteSingleTopic')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('deleteSingleTopic')
        await workbook.getAction('deleteSingleTopic').execute()
      },
    },
  ]

  const styleMenu: FrameMenuItem[] = [
    {
      label: $T('Copy Style'),
      enable: workbook.getAction('editor.copyStyle').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.copyStyle')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('copyStyle')
        await workbook.getAction('editor.copyStyle').execute()
      },
    },
    {
      label: $T('Paste Style'),
      enable: workbook.getAction('editor.pasteStyle').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.pasteStyle')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('pasteStyle')
        await workbook.getAction('editor.pasteStyle').execute()
      },
    },
    {
      label: $T('Reset Style'),
      enable: workbook.getAction('editor.resetStyle').isEnabled,
      shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.resetStyle')),
      action: async () => {
        trackNewVersionWorks.topicRightClick('resetStyle')
        await workbook.getAction('editor.resetStyle').execute()
      },
    },
  ]

  const showBranchOnlyMenu: FrameMenuItem[] = [
    {
      label: $T('Show Branch Only'),
      enable: true,
      shortcut: shortcutManager?.findDescriptionByActionName('showBranchOnly'),
      action: async () => {
        trackNewVersionWorks.topicRightClick('showBranchOnly')
      },
      isPro: true,
    },
  ]

  if (contexts.isInBrainstormEditor) {
    return [...topicMenu, ...(currentTargetTopicType !== 'root' ? deleteItemMenu : [])]
  }

  if (currentTargetTopicType === 'callout') {
    return [...deleteItemMenu, ...separatorMenu, ...styleMenu]
  }

  if (currentTargetTopicType === 'summary') {
    return [
      ...(isPhoneOnly() ? [] : [...makeInsertMenu(), ...separatorMenu]),
      ...topicMenu,
      ...separatorMenu,
      ...deleteItemMenu,
      ...(isPhoneOnly() ? [] : styleMenu),
      ...topicFoldMenu,
    ]
  }

  if (currentTargetTopicType === 'attached' || currentTargetTopicType === 'detached') {
    return [
      ...(isPhoneOnly() ? [] : [...makeInsertMenu(), ...separatorMenu]),
      ...topicMenu,
      ...separatorMenu,
      ...deleteItemMenu,
      ...deleteSingleTopicMenu,
      ...separatorMenu,
      ...(isPhoneOnly() ? [] : [...styleMenu, ...separatorMenu]),
      ...topicFoldMenu,
      ...topicFoldAllMenu,
      ...separatorMenu,
      ...showBranchOnlyMenu,
      ...separatorMenu,
      {
        label: $T('Reset Position'),
        enable: workbook.getAction('editor.resetPosition').isEnabled,
        shortcut: shortcutManager?.findDescriptionByAction(workbook.getAction('editor.resetPosition')),
        action: async () => {
          trackNewVersionWorks.topicRightClick('resetPosition')
          await workbook.getAction('editor.resetPosition').execute()
        },
      },
      ...createSheetFromSelectedMenu,
    ]
  }

  if (currentTargetTopicType === 'root') {
    return [
      ...(isPhoneOnly() ? [] : [...makeInsertMenu(), ...separatorMenu]),
      ...topicMenu,
      ...separatorMenu,
      ...(isPhoneOnly() ? [] : [...styleMenu, ...separatorMenu]),
      ...topicFoldAllMenu,
      ...separatorMenu,
      ...createSheetFromSelectedMenu,
    ]
  }

  return []
}
